/**
 * PATH: packages/job-hub/src/jobs/qa/qaDisplayNumbers.ts
 * FILE_ID: JOBHUB-QA-DISPLAY-NUMBERS-001
 * PURPOSE: Derive checklist and item display numbers from tree position.
 * WHY: Stored item_number / section_number are frozen at creation. Reorder
 *      and delete change sort_order but never the number, so the rail and
 *      the 1.2.x labels drift. Display numbers follow the array the API
 *      already returns in Meta.ordering.
 * LANDMINE: Never send display_number back to the API. Insert anchors use
 *      the stored item_number. An optimistic insert has no display_number
 *      until the next refetch and falls back to item_number.
 */
import type { QaAreaExecution, QaChecklistInstance, QaItemInstance } from './qaTypes'

function itemDisplayNumber(
  items: readonly QaItemInstance[],
  base: string,
): QaItemInstance[] {
  const parentNumbers = new Map<string, string>()
  const childCounts = new Map<string, number>()
  let top = 0
  return items.map((item) => {
    const parentKey = item.parent_item
    let number: string
    if (parentKey && parentNumbers.has(parentKey)) {
      const n = (childCounts.get(parentKey) ?? 0) + 1
      childCounts.set(parentKey, n)
      number = `${parentNumbers.get(parentKey)}.${n}`
    } else {
      top += 1
      number = base ? `${base}.${top}` : String(top)
    }
    if (item.template_item) parentNumbers.set(item.template_item, number)
    return { ...item, display_number: number }
  })
}

function checklistBase(areaCode: string, checklistCount: number, index: number): string {
  if (checklistCount === 1) return areaCode
  return [areaCode, String(index + 1)].filter(Boolean).join('.')
}

export function withQaDisplayNumbers<T extends QaAreaExecution>(area: T): T {
  const prefix = (area.area_code ?? '').trim()
  const checklists: QaChecklistInstance[] = area.checklists.map((checklist, index) => {
    const base = checklistBase(prefix, area.checklists.length, index)
    return {
      ...checklist,
      display_number: base || String(index + 1),
      items: itemDisplayNumber(checklist.items, base),
    }
  })
  return { ...area, checklists }
}
